/**
 * The GM lock — one password for the GM-only parts of the public site: the
 * story section and the bundled campaign packs.
 *
 * Two layers, because a static site has no server to ask:
 *
 *   1. The packs are ENCRYPTED on the server. `npm run build` runs
 *      tools/lock-samples.ts, which turns every dist/samples/*.atlagm and the
 *      pack list into an AES-256-GCM envelope keyed from the password
 *      (PBKDF2-SHA-256). Fetching a pack file by its URL yields ciphertext;
 *      only the password opens it. This is the real protection.
 *   2. The story section and the pack list are hidden behind a password
 *      prompt. The story shows the GM's own campaign from this browser, so
 *      the prompt keeps it from a player looking over the GM's screen — it
 *      is a curtain, not a vault.
 *
 * The site ships the password's SHA-256, never the password. The unlock
 * lasts for the browser tab's session (sessionStorage): closing the browser
 * locks it again, and "Kilitle" locks it at once.
 *
 * `gm123` is short; anyone who guesses it opens the packs. Change it with
 * ATLA_GM_PASSWORD at build time and put its SHA-256 here.
 */
import { useSyncExternalStore } from 'react';

/** SHA-256 of the GM password ("gm123"). */
export const GM_PASSWORD_SHA256 = '90dfdb54d9f99ec3209ad696ca75166fb3c187a47786e84f4ce1a995d623dba7';

const SESSION_KEY = 'atla-gm:gm-unlock:v1';
const LOCKED_FORMAT = 'atla-gm-locked';
const ITERATIONS = 150_000;

/* ------------------------------------------------------------ bytes */

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* ------------------------------------------------------------ envelope */

export interface LockedEnvelope {
  format: typeof LOCKED_FORMAT;
  version: 1;
  kdf: { name: 'PBKDF2-SHA-256'; iterations: number; salt: string };
  cipher: { name: 'AES-256-GCM'; iv: string };
  data: string;
}

export function isLockedEnvelope(value: unknown): value is LockedEnvelope {
  const v = value as Partial<LockedEnvelope> | null;
  return typeof v === 'object' && v !== null && v.format === LOCKED_FORMAT && typeof v.data === 'string';
}

async function deriveKey(password: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Encrypt any JSON value (the build's lock step; also the gate's round trip). */
export async function lockJson(value: unknown, password: string): Promise<LockedEnvelope> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt, ITERATIONS);
  const plain = encoder.encode(JSON.stringify(value));
  const sealed = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plain));
  return {
    format: LOCKED_FORMAT,
    version: 1,
    kdf: { name: 'PBKDF2-SHA-256', iterations: ITERATIONS, salt: toBase64(salt) },
    cipher: { name: 'AES-256-GCM', iv: toBase64(iv) },
    data: toBase64(sealed),
  };
}

export class WrongPasswordError extends Error {
  constructor() {
    super('wrong GM password');
    this.name = 'WrongPasswordError';
  }
}

/** Decrypt an envelope; a wrong password fails the GCM tag and throws WrongPasswordError. */
export async function unlockJson(envelope: LockedEnvelope, password: string): Promise<unknown> {
  const key = await deriveKey(password, fromBase64(envelope.kdf.salt), envelope.kdf.iterations);
  try {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(envelope.cipher.iv) as BufferSource },
      key,
      fromBase64(envelope.data) as BufferSource,
    );
    return JSON.parse(decoder.decode(plain)) as unknown;
  } catch {
    throw new WrongPasswordError();
  }
}

/** A fetched JSON file that may be locked: plain JSON passes through (dev server, older builds). */
export async function openMaybeLocked(value: unknown, password: string | null): Promise<unknown> {
  if (!isLockedEnvelope(value)) return value;
  if (!password) throw new WrongPasswordError();
  return unlockJson(value, password);
}

/* ------------------------------------------------------------ session state */

type Listener = () => void;
const listeners = new Set<Listener>();

function readSession(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

let current: string | null = readSession();

function emit(): void {
  for (const listener of listeners) listener();
}

/** The password this session unlocked with (packs need it to decrypt), or null. */
export function gmPassword(): string | null {
  return current;
}

export function isGmUnlocked(): boolean {
  return current !== null;
}

/** Check a typed password; on a match the session is unlocked. */
export async function tryUnlockGm(password: string): Promise<boolean> {
  const ok = (await sha256Hex(password)) === GM_PASSWORD_SHA256;
  if (!ok) return false;
  current = password;
  try {
    sessionStorage.setItem(SESSION_KEY, password);
  } catch {
    // private window: the unlock lasts until reload
  }
  emit();
  return true;
}

export function lockGm(): void {
  current = null;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // nothing stored
  }
  emit();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useGmUnlocked(): boolean {
  return useSyncExternalStore(subscribe, isGmUnlocked, isGmUnlocked);
}
